import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Hex } from 'viem';
import { AddressColorPipe } from '../../pipes/address-color.pipe';
import { ChallengeService } from '../../services/challenge.service';
import { ServerService } from '../../services/server.service';
import { TeamService } from '../../services/team.service';
import { addressEquals, shortenAddress } from '../../services/utils.service';
import { Wallet } from '../../services/wallet';
import { visibleInterval } from '../../utils/visible-interval';
import { IdentityComponent } from '../identity/identity.component';
import { PixelCreatureComponent } from '../pixel-creature/pixel-creature.component';

@Component({
  selector: 'app-queue-listings-strip',
  imports: [AddressColorPipe, IdentityComponent, PixelCreatureComponent],
  templateUrl: './queue-listings-strip.component.html',
  styleUrls: [
    './queue-listings-strip.component.css',
    './queue-listings-strip.component.mobile.css',
  ],
  host: { '[class.empty]': '!listings().length' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QueueListingsStripComponent {
  private readonly server = inject(ServerService);
  private readonly wallet = inject(Wallet);
  private readonly teams = inject(TeamService);
  protected readonly challenge = inject(ChallengeService);

  protected readonly nowMs = signal(Date.now());

  // The listing's handle is snapshotted when the player queued; resolveIdentity
  // backfills it from live presence so a later X link still shows.
  protected readonly listings = computed(() => {
    const me = this.wallet.activeAddress();
    return this.server
      .queueListings()
      .filter((l) => !addressEquals(l.address, me))
      .map((l) => this.server.resolveIdentity(l));
  });

  protected readonly canJoin = computed(
    () =>
      !this.challenge.isInQueue() &&
      !this.challenge.isSigning() &&
      this.teams.hasTeam(),
  );

  private readonly hasListings = computed(() => this.listings().length > 0);

  constructor() {
    // The waited timers are the only reader, so tick only while one is on screen.
    effect((onCleanup) => {
      if (!this.hasListings()) return;
      this.nowMs.set(Date.now());
      onCleanup(visibleInterval(1000, () => this.nowMs.set(Date.now())));
    });
  }

  protected readonly shortHex = shortenAddress;

  protected formatLabel(battleMode: number): string {
    return battleMode === 1 ? 'Doubles' : 'Singles';
  }

  protected waited(joinedAt: number): string {
    const secs = Math.max(0, Math.floor((this.nowMs() - joinedAt) / 1000));
    return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
  }

  protected async onJoin(address: Hex, battleMode: number): Promise<void> {
    if (!this.canJoin()) return;
    try {
      await this.challenge.joinSignedQueue({ battleMode, preferOpponent: address });
    } catch (error) {
      console.error('Error joining queue from listing:', error);
    }
  }
}
